import { X, FileText, Play } from 'lucide-react'

interface PendingAttachment {
  id: string
  file: File
  previewUrl: string | null
}

interface AttachmentPreviewProps {
  attachments: PendingAttachment[]
  onRemove: (id: string) => void
}

function isImage(file: File): boolean {
  // Browsers cannot render HEIC/HEIF natively
  if (file.type === 'image/heic' || file.type === 'image/heif') return false
  return file.type.startsWith('image/')
}

function isVideo(file: File): boolean {
  return file.type.startsWith('video/')
}

export default function AttachmentPreview({
  attachments,
  onRemove,
}: AttachmentPreviewProps) {
  if (attachments.length === 0) {
    return null
  }

  return (
    <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-t-lg border-b border-gray-200">
      {attachments.map((attachment) => (
        <div
          key={attachment.id}
          className="relative group"
        >
          {isImage(attachment.file) && attachment.previewUrl ? (
            <div className="w-20 h-20 rounded-lg overflow-hidden border border-gray-200 bg-white">
              <img
                src={attachment.previewUrl}
                alt={attachment.file.name}
                className="w-full h-full object-cover"
              />
            </div>
          ) : isVideo(attachment.file) ? (
            <div className="w-20 h-20 rounded-lg border border-gray-600 bg-gray-800 flex flex-col items-center justify-center relative">
              <Play className="w-8 h-8 text-white" />
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] text-center py-0.5 rounded-b-lg font-bold">
                VIDEO
              </div>
            </div>
          ) : (
            <div className="w-20 h-20 rounded-lg border border-gray-200 bg-white flex flex-col items-center justify-center p-2">
              <FileText className="w-8 h-8 text-gray-400" />
              <span className="text-[10px] text-gray-500 truncate max-w-full mt-1">
                {attachment.file.name.split('.').pop()?.toUpperCase()}
              </span>
            </div>
          )}

          {/* Remove button */}
          <button
            onClick={() => onRemove(attachment.id)}
            className="absolute -top-2 -right-2 w-5 h-5 bg-gray-800 hover:bg-gray-900 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
          >
            <X className="w-3 h-3" />
          </button>

          {/* File info tooltip */}
          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[9px] p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity truncate">
            {attachment.file.name}
          </div>
        </div>
      ))}
    </div>
  )
}
